import type {
  CategoryDto,
  TemplateDto,
  TransactionDto,
  WalletDto,
} from '@esportsbudget/shared';
import { BookmarkPlus, Paperclip, X } from 'lucide-react';
import { fromMinor, todayISO } from '@esportsbudget/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '@/components/ui/Button';
import { Input, Textarea } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { MoneyInput, moneyInputToMinor } from '@/components/ui/MoneyInput';
import { SegmentedControl, Select } from '@/components/ui/Select';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { errorMessage } from '@/lib/errorMessage';

type Direction = 'IN' | 'OUT';

/**
 * Добавление операции.
 *
 * Быстрый сценарий — четыре поля: сумма, тип, кошелёк, категория.
 * Дата подставляется сегодняшняя, всё остальное скрыто за «Подробнее».
 */
export function TransactionFormModal({
  open,
  transaction,
  wallets,
  categories,
  defaultWalletId,
  onClose,
  onSaved,
}: {
  open: boolean;
  transaction: TransactionDto | null;
  wallets: WalletDto[];
  categories: CategoryDto[];
  defaultWalletId?: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation(['finance', 'common', 'errors']);
  const queryClient = useQueryClient();
  const isEdit = Boolean(transaction);

  /**
   * Шаблоны операций (F-09).
   *
   * Аренда буткемпа, взнос за турнир, оплата сервера — это одни и те же
   * операции месяц за месяцем. При изменении операции шаблоны не нужны:
   * там уже всё заполнено, и подставлять поверх было бы вредно.
   */
  const templatesQuery = useQuery({
    queryKey: ['templates'],
    queryFn: () => api.get<TemplateDto[]>('/templates'),
    enabled: open && !isEdit,
  });

  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');

  const activeWallets = useMemo(() => wallets.filter((wallet) => !wallet.isArchived), [wallets]);

  const [direction, setDirection] = useState<Direction>('OUT');
  const [walletId, setWalletId] = useState('');
  const [amount, setAmount] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [date, setDate] = useState(todayISO());
  const [counterparty, setCounterparty] = useState('');
  const [description, setDescription] = useState('');
  const [advanced, setAdvanced] = useState(false);

  useEffect(() => {
    if (!open) return;

    if (transaction) {
      setDirection(transaction.direction);
      setWalletId(transaction.walletId);
      setAmount(String(fromMinor(transaction.amountMinor, transaction.currency)));
      setCategoryId(transaction.categoryId ?? '');
      setDate(transaction.occurredAt);
      setCounterparty(transaction.counterparty ?? '');
      setDescription(transaction.description ?? '');
      setAdvanced(Boolean(transaction.counterparty || transaction.description));
    } else {
      setDirection('OUT');
      setWalletId(defaultWalletId ?? activeWallets[0]?.id ?? '');
      setAmount('');
      setCategoryId('');
      setDate(todayISO());
      setCounterparty('');
      setDescription('');
      setAdvanced(false);
    }
  }, [open, transaction, defaultWalletId, activeWallets]);

  function applyTemplate(template: TemplateDto) {
    const payload = template.payload;

    setDirection(payload.direction);
    if (payload.walletId) setWalletId(payload.walletId);
    if (payload.categoryId) setCategoryId(payload.categoryId);
    setCounterparty(payload.counterparty ?? '');
    setDescription(payload.description ?? '');
    setAdvanced(Boolean(payload.counterparty || payload.description));

    // Сумму подставляем, только если она в шаблоне есть: у аренды она
    // постоянна, у закупки — каждый раз своя, и ноль там был бы враньём.
    if (payload.amountMinor) {
      const target = activeWallets.find((item) => item.id === (payload.walletId ?? walletId));
      setAmount(String(fromMinor(payload.amountMinor, target?.currency ?? 'RUB')));
    }

    // Отмечаем применение: часто используемые шаблоны сами поднимаются вверх.
    void api.post(`/templates/${template.id}/used`).catch(() => undefined);
  }

  const saveTemplateMutation = useMutation({
    mutationFn: () =>
      api.post<TemplateDto>('/templates', {
        name: templateName.trim(),
        payload: {
          direction,
          walletId: walletId || null,
          categoryId: categoryId || null,
          counterparty: counterparty.trim() || null,
          description: description.trim() || null,
          isPersonCost: false,
          amountMinor: null,
        },
      }),
    onSuccess: () => {
      toast.success(t('finance:templateSaved'));
      setSavingTemplate(false);
      setTemplateName('');
      void queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const deleteTemplateMutation = useMutation({
    mutationFn: (templateId: string) => api.delete(`/templates/${templateId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const templates = templatesQuery.data ?? [];

  const wallet = activeWallets.find((item) => item.id === walletId);
  const currency = wallet?.currency ?? 'RUB';
  const amountMinor = moneyInputToMinor(amount, currency);

  // Категории показываем только подходящие по типу операции — иначе
  // сервер вернёт ошибку уже после отправки формы.
  const fittingCategories = categories.filter(
    (category) => category.kind === (direction === 'IN' ? 'INCOME' : 'EXPENSE'),
  );

  useEffect(() => {
    if (categoryId && !fittingCategories.some((category) => category.id === categoryId)) {
      setCategoryId('');
    }
  }, [direction, categoryId, fittingCategories]);

  // Чек прикладывается вторым запросом: сначала операция должна получить id.
  // Пользователю этого не видно — он просто нажимает «Добавить».
  const [receipt, setReceipt] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = {
        direction,
        amountMinor: amountMinor ?? 0,
        occurredAt: date,
        categoryId: categoryId || null,
        counterparty: counterparty.trim() || null,
        description: description.trim() || null,
        isPersonCost: false,
      };

      const saved = transaction
        ? await api.patch<TransactionDto>(`/transactions/${transaction.id}`, payload)
        : await api.post<TransactionDto>('/transactions', { ...payload, walletId });

      if (receipt) {
        const form = new FormData();
        form.append('file', receipt);
        await api.upload(`/transactions/${saved.id}/attachment`, form);
      }

      return saved;
    },
    onSuccess: () => {
      toast.success(isEdit ? t('common:saved') : t('finance:transactionAdded'));
      setReceipt(null);
      onSaved();
      onClose();
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const canSubmit =
    Boolean(walletId) && amountMinor !== null && amountMinor > 0 && !mutation.isPending;

  return (
    <Modal
      open={open}
      onClose={onClose}
      busy={mutation.isPending}
      title={isEdit ? t('finance:editTransaction') : t('finance:addTransaction')}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            {t('common:cancel')}
          </Button>
          <Button loading={mutation.isPending} disabled={!canSubmit} onClick={() => mutation.mutate()}>
            {isEdit ? t('common:save') : t('common:add')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {/* Шаблоны — первое, что видно: смысл в том, чтобы не заполнять
            форму, а не в том, чтобы заполнить её и найти кнопку внизу. */}
        {!isEdit && templates.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {templates.map((template) => (
              <span
                key={template.id}
                className="group inline-flex items-center rounded-full border border-border bg-surface-2 pl-3 pr-1 text-[12.5px] transition-colors hover:border-accent"
              >
                <button
                  type="button"
                  className="py-1.5 pr-1 font-medium"
                  title={
                    [template.walletName, template.categoryName].filter(Boolean).join(' · ') ||
                    undefined
                  }
                  onClick={() => applyTemplate(template)}
                >
                  {template.name}
                </button>
                <button
                  type="button"
                  aria-label={t('common:delete')}
                  className="rounded-full p-1 text-subtle opacity-0 transition-opacity hover:text-danger group-hover:opacity-100"
                  onClick={() => deleteTemplateMutation.mutate(template.id)}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}

        <SegmentedControl<Direction>
          value={direction}
          onChange={setDirection}
          options={[
            { value: 'OUT', label: t('finance:expense') },
            { value: 'IN', label: t('finance:income') },
          ]}
        />

        <MoneyInput
          label={t('finance:amount')}
          value={amount}
          onChange={setAmount}
          currency={currency}
          large
          autoFocus
          hint={t('finance:amountCalcHint')}
          onEnter={() => canSubmit && mutation.mutate()}
        />

        <div className="grid gap-3 sm:grid-cols-2">
          <Select
            label={t('finance:wallet')}
            options={activeWallets.map((item) => ({ value: item.id, label: item.name }))}
            value={walletId}
            disabled={isEdit}
            hint={isEdit ? t('finance:walletLocked') : undefined}
            onChange={(event) => setWalletId(event.target.value)}
          />

          <Input
            type="date"
            label={t('finance:date')}
            value={date}
            onChange={(event) => setDate(event.target.value)}
          />
        </div>

        <Select
          label={t('finance:category')}
          placeholder={t('finance:noCategory')}
          options={[
            { value: '', label: t('finance:noCategory') },
            ...fittingCategories.map((category) => ({ value: category.id, label: category.name })),
          ]}
          value={categoryId}
          onChange={(event) => setCategoryId(event.target.value)}
        />

        {advanced ? (
          <>
            <Input
              label={t('finance:counterparty')}
              placeholder={t('finance:counterpartyPlaceholder')}
              value={counterparty}
              maxLength={120}
              onChange={(event) => setCounterparty(event.target.value)}
            />

            <Textarea
              label={t('finance:comment')}
              placeholder={t('finance:commentPlaceholder')}
              value={description}
              maxLength={500}
              onChange={(event) => setDescription(event.target.value)}
            />
          </>
        ) : (
          <button
            type="button"
            onClick={() => setAdvanced(true)}
            className="self-start text-[13px] font-medium text-accent hover:underline"
          >
            {t('common:details')} ▾
          </button>
        )}

        {/* Сохранение заготовки. Сумму в шаблон не кладём: она меняется
            чаще всего остального, и шаблон с ней устаревал бы сразу. */}
        {!isEdit &&
          (savingTemplate ? (
            <div className="flex items-end gap-2 rounded-[10px] border border-border p-3">
              <Input
                label={t('finance:templateName')}
                placeholder={t('finance:templateNamePlaceholder')}
                value={templateName}
                maxLength={80}
                autoFocus
                className="flex-1"
                onChange={(event) => setTemplateName(event.target.value)}
              />
              <Button
                size="sm"
                disabled={!templateName.trim()}
                loading={saveTemplateMutation.isPending}
                onClick={() => saveTemplateMutation.mutate()}
              >
                {t('common:save')}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setSavingTemplate(false)}>
                {t('common:cancel')}
              </Button>
            </div>
          ) : (
            (categoryId || counterparty.trim() || description.trim()) && (
              <button
                type="button"
                onClick={() => setSavingTemplate(true)}
                className="inline-flex items-center gap-1.5 self-start text-[13px] font-medium text-muted hover:text-accent"
              >
                <BookmarkPlus className="h-3.5 w-3.5" aria-hidden />
                {t('finance:saveAsTemplate')}
              </button>
            )
          ))}

        {/* Чек прямо в форме (F-33, шаг [3.10]). Прикладывать его потом,
            отдельным действием, никто не будет — а через месяц он и понадобится. */}
        <div
          onDragOver={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => {
            event.preventDefault();
            setDragging(false);
            const dropped = event.dataTransfer?.files?.[0];
            if (dropped) setReceipt(dropped);
          }}
          onPaste={(event) => {
            // Скриншот чека вставляют из буфера чаще, чем открывают проводник.
            const pasted = event.clipboardData?.files?.[0];
            if (pasted) {
              event.preventDefault();
              setReceipt(pasted);
            }
          }}
          className={cn(
            'rounded-[10px] border border-dashed p-3 transition-colors',
            dragging ? 'border-accent bg-accent-soft' : 'border-border',
          )}
        >
          {receipt || transaction?.hasAttachment ? (
            <div className="flex items-center gap-2 text-[13px]">
              <Paperclip className="h-4 w-4 shrink-0 text-muted" aria-hidden />
              <span className="min-w-0 flex-1 truncate">
                {receipt?.name ?? transaction?.attachmentName}
              </span>
              {receipt ? (
                <button
                  type="button"
                  onClick={() => setReceipt(null)}
                  className="shrink-0 text-[12px] text-muted hover:text-danger"
                >
                  {t('common:remove')}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInput.current?.click()}
                  className="shrink-0 text-[12px] text-accent hover:underline"
                >
                  {t('finance:replaceReceipt')}
                </button>
              )}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              className="flex w-full items-center gap-2 text-left text-[13px] text-muted"
            >
              <Paperclip className="h-4 w-4 shrink-0" aria-hidden />
              <span>{t('finance:attachReceipt')}</span>
            </button>
          )}

          <input
            ref={fileInput}
            type="file"
            hidden
            accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
            onChange={(event) => {
              const picked = event.target.files?.[0];
              if (picked) setReceipt(picked);
              event.target.value = '';
            }}
          />
        </div>
      </div>
    </Modal>
  );
}
